import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEnvironmentConversation } from "@/hooks/use-environment-conversation";
import { useEnvironmentName } from "@/hooks/use-environment-name";
import { useGetAgentExecution } from "@/queries/agent-queries";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionName } from "@/utils/agent-executions";

/**
 * Hook that dynamically sets the document title based on the current route and context.
 * When viewing an environment or conversation, it uses the environment/conversation title
 * that's shown in the sidebar. Otherwise, it falls back to a default title.
 *
 * Optimized to prevent infinite loops and performance issues in tests.
 */
export function useDynamicTitle(fallbackTitle?: string) {
    const params = useParams();

    // Extract IDs from route params
    const { environmentId, agentExecutionId } = params;

    // Only fetch data if we have the relevant IDs to avoid unnecessary queries
    const { data: environment } = useEnvironment(environmentId);

    const { data: agentExecution } = useGetAgentExecution(agentExecutionId);

    // Get environment conversation context only if we have an environment
    const { environmentConversation } = useEnvironmentConversation(environmentId);

    // Get environment name (same logic as used in sidebar)
    const environmentName = useEnvironmentName(environment, agentExecution);

    // Determine the dynamic title based on current route and context
    const dynamicTitle = useMemo(() => {
        // If we're viewing a specific environment and have data
        if (environmentId && environment) {
            // A custom environment name (set via rename) always takes priority
            if (environment.metadata?.name) {
                return environment.metadata.name;
            }
            // If there's an active conversation, prioritize the conversation title
            if (environmentConversation) {
                switch (environmentConversation.type) {
                    case "remote-agent-execution":
                        return agentExecutionName(environmentConversation.agentExecution);
                    case "in-environment-agent-execution":
                        return environmentConversation.agentExecution
                            ? agentExecutionName(environmentConversation.agentExecution)
                            : environmentName;
                    case "onboarding":
                        return environmentName;
                    default:
                        return environmentName;
                }
            }
            // Otherwise use the environment name
            return environmentName;
        }

        // If we're viewing a standalone agent execution and have data
        if (agentExecutionId && agentExecution) {
            return agentExecutionName(agentExecution);
        }

        // For other routes, return the fallback
        return fallbackTitle;
    }, [
        environmentId,
        environment,
        environmentConversation,
        environmentName,
        agentExecutionId,
        agentExecution,
        fallbackTitle,
    ]);

    // Set the document title - useDocumentTitle handles undefined/empty values gracefully
    // If the title is "Ona", use removeSuffix to prevent "Ona | Ona" duplication
    const shouldRemoveSuffix = dynamicTitle === "Ona";
    useDocumentTitle(dynamicTitle, shouldRemoveSuffix);

    return dynamicTitle;
}
